import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PaymentStatus = "active" | "expiring" | "expired" | "none";

export interface AttendanceLog {
  id: string;
  user_id: string;
  gym_id: string;
  check_in_at: string;
  created_at: string;
  payment_status: PaymentStatus | null;
  days_remaining: number | null;
  user?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    dni: string | null;
  };
}

interface UseAttendanceLogsOptions {
  gymId: string;
  page?: number;
  pageSize?: number;
  dateFrom?: Date;
  dateTo?: Date;
}

export const useAttendanceLogs = ({
  gymId,
  page = 1,
  pageSize = 20,
  dateFrom,
  dateTo,
}: UseAttendanceLogsOptions) => {
  return useQuery({
    queryKey: ["attendance-logs", gymId, page, pageSize, dateFrom?.toISOString(), dateTo?.toISOString()],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("gym_attendance_logs")
        .select(`
          id,
          user_id,
          gym_id,
          check_in_at,
          created_at,
          payment_status,
          days_remaining
        `, { count: "exact" })
        .eq("gym_id", gymId)
        .order("check_in_at", { ascending: false })
        .range(from, to);

      if (dateFrom) {
        const startOfDay = new Date(dateFrom);
        startOfDay.setHours(0, 0, 0, 0);
        query = query.gte("check_in_at", startOfDay.toISOString());
      }
      if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.lte("check_in_at", endOfDay.toISOString());
      }

      const { data: logs, error, count } = await query;

      if (error) throw error;

      // Fetch user details for each log
      const userIds = [...new Set(logs?.map(log => log.user_id) || [])];
      
      const [profilesResult, pendingMembersResult] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, first_name, last_name, avatar_url, dni")
          .in("id", userIds),
        supabase
          .from("pending_members")
          .select("id, first_name, last_name, dni")
          .in("id", userIds),
      ]);

      const { data: profiles } = profilesResult;
      const { data: pendingMembers } = pendingMembersResult;

      const userMap = new Map<string, AttendanceLog["user"]>();
      
      profiles?.forEach(p => {
        userMap.set(p.id, {
          id: p.id,
          first_name: p.first_name,
          last_name: p.last_name,
          avatar_url: p.avatar_url,
          dni: p.dni,
        });
      });

      pendingMembers?.forEach(pm => {
        if (!userMap.has(pm.id)) {
          userMap.set(pm.id, {
            id: pm.id,
            first_name: pm.first_name,
            last_name: pm.last_name,
            avatar_url: null,
            dni: pm.dni,
          });
        }
      });

      // Map logs with user data - status comes from DB now!
      const logsWithUsers: AttendanceLog[] = (logs || []).map(log => ({
        ...log,
        payment_status: (log.payment_status as PaymentStatus) || null,
        days_remaining: log.days_remaining,
        user: userMap.get(log.user_id),
      }));

      return {
        logs: logsWithUsers,
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
    enabled: !!gymId,
    refetchInterval: 30000,
  });
};
